import React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';

/**
 * SitePageBody — the ONE renderer for operator-authored page bodies
 * (SitePage.content_md and friends: Terms, Privacy, Shipping Policy,
 * FAQ, About Us).
 *
 * Why this exists (bug, prod 2026-09-08): those bodies are edited by a
 * human, and in practice some of them are markdown while others are raw
 * HTML — prod's `terms` and `privacy-policy` are HTML (`<p><em>Effective
 * date…</em></p><h2>1. Overview</h2>…`) while `shipping-policy` is real
 * markdown. Plain `react-markdown` escapes embedded HTML by default, so
 * the HTML pages rendered their tags to the customer as literal text.
 *
 * The 2026-07-18 security fix that removed `dangerouslySetInnerHTML`
 * from these pages stays removed. Instead the raw HTML is parsed
 * (rehype-raw) and then sanitized (rehype-sanitize, GitHub default
 * schema) BEFORE it becomes React elements: `<script>` is stripped
 * outright, event handlers (`onclick=`) and unknown attributes are
 * dropped, `<iframe>`/`<object>`/`<style>` are not allowed tag names,
 * and `href`/`src` are protocol-checked so `javascript:` URLs are
 * neutralised. Plugin order matters — raw MUST run before sanitize.
 *
 * Links may opt into `target="_blank"` (any casing), but only ever
 * paired with `rel="noopener noreferrer"`, which we inject rather than
 * trust from the authored markup. Any other target value is dropped.
 */

// GitHub default schema, plus `target` on anchors. `rel` is deliberately
// NOT allowed through from the authored HTML — we set it ourselves below.
//
// `strip` is the "delete the element AND its text" list, as opposed to
// the default behaviour for a disallowed tag, which is to unwrap it and
// keep the children. The GitHub schema ships `['script']`; `style` and
// `noscript` must join it or a stray `<style>` block renders its CSS
// source as visible page copy (and a `<noscript>` renders its fallback
// markup unconditionally).
export const SITE_PAGE_SANITIZE_SCHEMA = {
  ...defaultSchema,
  strip: [...new Set([...(defaultSchema.strip || []), 'style', 'noscript'])],
  attributes: {
    ...defaultSchema.attributes,
    a: [...(defaultSchema.attributes?.a || []), 'target'],
  },
};

// Module-level constants: stable identities keep react-markdown from
// rebuilding its processor on every render.
const REHYPE_PLUGINS = [rehypeRaw, [rehypeSanitize, SITE_PAGE_SANITIZE_SCHEMA]];

const COMPONENTS = {
  a({ node, target, children, ...props }) {
    // HTML target values are case-insensitive, so `_BLANK` is the same
    // request as `_blank`. Everything else an author might write —
    // `_parent`, `_top`, a named frame — is dropped rather than passed
    // through; a policy page has no legitimate use for them.
    if (String(target).toLowerCase() === '_blank') {
      return (
        <a {...props} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    }
    return <a {...props}>{children}</a>;
  },
};

// Batch 34 C2 (Cowork run 32): the operator's bodies open with their own
// heading — "# Frequently Asked Questions", "# Shipping Policy", "# About
// Feeling Froggy" — under a page that already prints that title as its h1,
// so the customer read the title twice. The fix is HERE, at the renderer,
// never in the operator's copy: when the body's FIRST heading repeats the
// page title (case- and whitespace-insensitive, markdown or HTML), that one
// heading is not rendered and the page keeps exactly one h1.
//
// It is a STRING split, not a rehype plugin, on purpose: it must hold in
// Jest, where react-markdown is a passthrough mock, and it must be the same
// reading on both sides of that mock.
const stripTags = (s) => String(s || '').replace(/<[^>]*>/g, '');
const ENTITIES = { '&amp;': '&', '&nbsp;': ' ', '&#39;': "'", '&quot;': '"', '&rsquo;': "'", '&lsquo;': "'" };

/** The one reading of "the same heading": markup, emphasis, entities,
 *  case and whitespace do not count. An empty heading matches nothing. */
export const headingKey = (s) => stripTags(s)
  .replace(/&[a-z#0-9]+;/gi, (e) => ENTITIES[e.toLowerCase()] ?? ' ')
  .replace(/[‘’]/g, "'")
  .replace(/[*_`]/g, '')
  .replace(/\s+/g, ' ')
  .trim()
  .toLowerCase();

export const headingsMatch = (a, b) => {
  const ka = headingKey(a);
  return ka !== '' && ka === headingKey(b);
};

// A markdown ATX heading ("# Title", optional closing #s) or an HTML
// <h1>-<h6> as the body's first non-blank content. Anything else — a
// paragraph, an effective-date line — means the body has no leading heading.
const ATX_LEAD = /^\s*#{1,6}[ \t]+([^\r\n]*?)[ \t]*#*[ \t]*(?:\r?\n|$)/;
const HTML_LEAD = /^\s*<h([1-6])\b[^>]*>([\s\S]*?)<\/h\1\s*>/i;

/** { text, rest } for the body's leading heading, or null. */
export function leadingHeading(content) {
  const src = String(content || '');
  const md = src.match(ATX_LEAD);
  if (md) return { text: md[1].trim(), rest: src.slice(md[0].length) };
  const html = src.match(HTML_LEAD);
  if (html) return { text: stripTags(html[2]).trim(), rest: src.slice(html[0].length) };
  return null;
}

/** The body without its leading heading when that heading repeats `title`. */
export function bodyWithoutRepeatedTitle(content, title) {
  if (!content || !title) return content;
  const lead = leadingHeading(content);
  return lead && headingsMatch(lead.text, title) ? lead.rest : content;
}

/**
 * @param {object} props
 * @param {string} [props.content]   Operator-authored markdown OR HTML.
 * @param {string} [props.title]     The page's own h1. A leading heading in
 *                                   the body that repeats it is not rendered.
 * @param {string} [props.className] Wrapper class (kept per-page so the
 *                                   existing token styles still apply).
 *                                   `ff-prose` — the ONE prose style of the
 *                                   text pages (global.css) — always rides.
 */
const SitePageBody = ({ content, title, className, ...rest }) => {
  if (!content) return null;
  const body = bodyWithoutRepeatedTitle(content, title);

  return (
    <div className={['ff-prose', className].filter(Boolean).join(' ')} {...rest}>
      <ReactMarkdown rehypePlugins={REHYPE_PLUGINS} components={COMPONENTS}>
        {body}
      </ReactMarkdown>
    </div>
  );
};

export default SitePageBody;
